import classNames from "classnames";
import { ESubStep } from "common/enums/Subscription/ESubStep";
import ProductSubscribedResponseResource from "common/resources/ProductSubscribed/ProductSubscribedResponseResource";
import ProductSubscriptionUpdateResource from "common/resources/ProductSubscribed/ProductSubscriptionUpdateResource";
import SubscriptionResponseResource from "common/resources/Subscription/SubscriptionResponseResource";
import UserPublicResponseResource from "common/resources/User/UserPublicResponseResource";
import Button, { EButtonVariant } from "components/elements/Button";
import NavTabOutlet from "components/elements/NavTabOutlet";
import Typography, { ITypo } from "components/elements/Typography";
import MuiSelectInputElement, { IMuiSelectOption } from "components/materials/Form/MuiSelectInputElement";
import I18n from "components/materials/I18n";
import PageTemplate from "components/PageTemplate";
import ModuleConfig from "configs/ModuleConfig";
import useOpenable from "hooks/useOpenable";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProductSubscribedService from "services/ProductSubscribedService";
import SubscriptionService from "services/SubscriptionService";
import UserService from "services/UserService";
import { container } from "tsyringe";
import NumberUtils from "utils/NumberUtils";
import ValidationDateModal from "../MOSubscriptions/tabs/elements/ValidationDateModal";
import classes from "./classes.module.scss";
import DateUtils from "utils/DateUtils";
import StatusTracker from "components/materials/StatusTracker";
import { EDocumentValidationStatus } from "common/enums/Subscription/EDocumentValidationStatus";
import { ESubscriptionProductType } from "common/enums/ProductSubscribed/ESubscriptionProductType";
import { EDemembrementType } from "common/enums/ProductSubscribed/EDemembrementType";
import { getViagerRatio } from "utils/baremeViager";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ArchiveSubscriptionModal from "./ArchiveSubscriptionModal";
import ContrepartieModal from "./ContrepartieModal";
import { LockClosedIcon, PencilSquareIcon } from "@heroicons/react/24/outline";

export type IMOSubscriptionSummaryContext = {
	productSubscribed: ProductSubscribedResponseResource | null;
	subscription: SubscriptionResponseResource | null;
};

const modules = container.resolve(ModuleConfig).get().modules;
const productSuscribedService = container.resolve(ProductSubscribedService);
const userService = container.resolve(UserService);
const subscriptionService = container.resolve(SubscriptionService);
const productSubscribedService = container.resolve(ProductSubscribedService);

export default function MOSubscriptionSummary() {
	const { productSubscribedId } = useParams();
	const [openSuccessAlert, setOpenSuccessAlert] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: "" });
	const [productSubscribed, setProductSubscribed] = useState<ProductSubscribedResponseResource | null>(null);
	const [subscription, setSubscription] = useState<SubscriptionResponseResource | null>(null);
	const [cgp, setCgp] = useState<UserPublicResponseResource | null>(null);
	const [hasEsignature, setHasEsignature] = useState<boolean>(false);
	const { isOpen, close, open } = useOpenable();
	const archiveSubscriptionModal = useOpenable();
	const contrepartieModal = useOpenable();
	const navigate = useNavigate();

	const outletContext: IMOSubscriptionSummaryContext = { productSubscribed, subscription };

	useEffect(() => {
		if (!productSubscribedId) return;
		productSuscribedService.getByUid(productSubscribedId).then((product) => setProductSubscribed(product));
	}, [productSubscribedId]);

	useEffect(() => {
		if (!productSubscribed) return;
		let idToUse = productSubscribed.subscriber?.creatorId ?? productSubscribed.corporationSubscriber?.creatorId;
		if (!idToUse) return;
		userService.getByIdAdmin(idToUse).then((cgp) => setCgp(cgp));
	}, [productSubscribed]);

	const cgpName = cgp ? cgp?.lastName + " " + cgp?.firstName : "-";

	const statusOptions = useMemo(() => {
		return Object.values(ESubStep)
			.map((step) => {
				const option: IMuiSelectOption = { id: step, label: I18n.asset.enums.ESubStepMO[step] };
				return option;
			})
			.filter((option) => option.id !== ESubStep.draft);
	}, []);

	useEffect(() => {
		if (!productSubscribed) return;

		subscriptionService.getByUid(productSubscribed.subscriptionId).then((subscription) => {
			setSubscription(subscription);
		});
	}, [productSubscribed]);

	useEffect(() => {
		if (!productSubscribed) return;
		subscriptionService.hasOngoingSignatureMO(productSubscribed.subscriptionId).then(setHasEsignature);
	}, [productSubscribed]);

	const updateStatus = useCallback(
		(id: string, step: ESubStep | null) => {
			if (!step) return;
			if (step !== ESubStep.validated) {
				ProductSubscriptionUpdateResource.hydrate<ProductSubscriptionUpdateResource>({ step })
					.validateOrReject()
					.then((resource) =>
						productSubscribedService
							.updateStatus(id, resource)
							.then((productSuscribedUpdated) =>
								setProductSubscribed((prev) =>
									ProductSubscribedResponseResource.hydrate<ProductSubscribedResponseResource>({ ...prev, ...productSuscribedUpdated }),
								),
							),
					);
			} else {
				open();
			}
		},
		[open],
	);

	const onValidationDateSubmitSuccess = useCallback(() => {
		if (!productSubscribed) return;
		productSuscribedService.getByUid(productSubscribed.id).then((product) => setProductSubscribed(product));
	}, [productSubscribed]);

	const onStatusChange = useCallback(
		(_event: React.SyntheticEvent<Element, Event>, value: IMuiSelectOption | null) => {
			if (!productSubscribed) return;
			updateStatus(productSubscribed.id, value ? (value.id as ESubStep) : null);
		},
		[productSubscribed, updateStatus],
	);

	const navigateToUploadDocuments = useCallback(() => {
		if (!productSubscribed) return;
		navigate(modules.pages.UploadDocumentsMo.props.path.replace(":productSubscribedId", productSubscribed.id));
	}, [productSubscribed, navigate]);

	const sendLegalDocs = useCallback(() => {
		if (!subscription || hasEsignature) return;
		subscriptionService
			.sendLegalDocs(subscription.id)
			.then(() => {
				setOpenSuccessAlert({
					isOpen: true,
					message: I18n.asset.pages.mo_subscription_summary.success_send_legal_docs,
				});
				console.log("Legal documents sent successfully");
			})
			.catch((error) => {
				console.error("Error sending legal documents:", error);
			});
	}, [subscription, hasEsignature]);

	const dueDate = useMemo(
		() => DateUtils.getDueDate(productSubscribed?.validationDate, productSubscribed?.productSheet?.dueDays, productSubscribed?.productSheet?.dueMonths),
		[productSubscribed],
	);

	const payoutDate = useMemo(() => {
		let dateForPayout = dueDate;
		if (
			productSubscribed?.subscriptionProductType === ESubscriptionProductType.nue_propriete &&
			productSubscribed?.demembrementType === EDemembrementType.temporaire &&
			productSubscribed?.demembrementDuration &&
			dueDate
		) {
			dateForPayout = new Date(dueDate);
			dateForPayout.setFullYear(dateForPayout.getFullYear() + productSubscribed.demembrementDuration);
		}
		return DateUtils.getPayoutDate(dateForPayout, productSubscribed?.productSheet?.distributionFrequency);
	}, [dueDate, productSubscribed]);

	const demembrementEndDate = useMemo(() => {
		const type = productSubscribed?.subscriptionProductType;
		if (!type || type === ESubscriptionProductType.pleine_propriete) return null;
		if (productSubscribed?.demembrementType !== EDemembrementType.temporaire) return null;
		if (!productSubscribed?.demembrementDuration || !dueDate) return null;
		const endDate = new Date(dueDate);
		endDate.setFullYear(endDate.getFullYear() + productSubscribed.demembrementDuration);
		return endDate;
	}, [productSubscribed, dueDate]);

	const subscriptionTypeLabel = useMemo(() => {
		const type = productSubscribed?.subscriptionProductType;
		if (!type || type === ESubscriptionProductType.pleine_propriete) return I18n.asset.enums.ESubscriptionProductType[ESubscriptionProductType.pleine_propriete];
		const baseLabel = I18n.asset.enums.ESubscriptionProductType[type];
		return productSubscribed?.demembrementType === EDemembrementType.viager ? `${baseLabel} (viager)` : baseLabel;
	}, [productSubscribed]);

	const detentionPercentage = useMemo(() => {
		const type = productSubscribed?.subscriptionProductType;
		if (!type || type === ESubscriptionProductType.pleine_propriete) return "100 %";
		if (productSubscribed?.demembrementType === EDemembrementType.viager && productSubscribed?.viagerAge != null) {
			const ratio = getViagerRatio(productSubscribed.viagerAge, type);
			return ratio !== null ? `${(ratio * 100).toFixed(2)} %` : "-";
		}
		const key = productSubscribed?.productSheet?.demembrementKeys?.find((k) => k.duration === productSubscribed?.demembrementDuration);
		if (!key) return "-";
		const ratio = type === ESubscriptionProductType.usufruit ? key.usufruitRatio : key.nueProprieteRatio;
		return `${(ratio * 100).toFixed(2)} %`;
	}, [productSubscribed]);

	const getManuallySentLegalDocumentsAt = useCallback(() => {
		if (!productSubscribed) return null;
		return productSubscribed.subscription?.manuallySentLegalDocumentsAt ? new Date(productSubscribed.subscription.manuallySentLegalDocumentsAt).toLocaleDateString() : null;
	}, [productSubscribed]);

	const getManuallyTransferToSGAt = useCallback(() => {
		if (!productSubscribed) return null;
		return productSubscribed.manuallyTransferToSGAt ? new Date(productSubscribed.manuallyTransferToSGAt).toLocaleDateString() : null;
	}, [productSubscribed]);

	const notifyCGPTransfer = useCallback(() => {
		if (!productSubscribedId) return;
		productSubscribedService
			.notifyReadyForSGTransfer(productSubscribedId)
			.then(() => {
				setOpenSuccessAlert({
					isOpen: true,
					message: I18n.asset.pages.mo_subscription_summary.success_notify_cgp_transfer,
				});
				console.log("CGP notified for transfer successfully");
			})
			.catch((error) => {
				console.error("Error notifying CGP for transfer:", error);
			});
	}, [productSubscribedId]);

	const legacyHistoryPath = useMemo(() => {
		if (!productSubscribed?.isLegacy) return null;
		if (productSubscribed.subscriber?.clientId) {
			return modules.pages.Clients.props.pages.PhysicalPersonClients.props.pages.PhysicalClientDetails.props.pages.LegacyHistory.props.path.replace(
				":clientId",
				productSubscribed.subscriber.clientId,
			);
		}
		if (productSubscribed.corporationSubscriber?.corporationId) {
			return modules.pages.Clients.props.pages.Corporations.props.pages.CorporationDetails.props.pages.LegacyHistory.props.path.replace(
				":corporationId",
				productSubscribed.corporationSubscriber.corporationId,
			);
		}
		return null;
	}, [productSubscribed]);

	if (!productSubscribedId) return null;
	if (!productSubscribed) return null;

	return (
		<PageTemplate
			tabTitle={I18n.asset.pages.mo_subscription_summary.page_title}
			headerTitle={I18n.asset.pages.mo_subscription_summary.title}
				rightElement={
				<div className={classes["button-container"]}>
					<Button onClick={archiveSubscriptionModal.open}>
						{I18n.asset.pages.mo_subscription_summary.archive}
					</Button>
					<Button disabled={productSubscribed.legacyReadOnly} onClick={navigateToUploadDocuments}>
						{I18n.asset.pages.mo_subscription_summary.document_management}
					</Button>
				</div>
			}>
			<div className={classes["root"]}>
				{productSubscribed.isLegacy && (
					<div className={classes["legacy-banner"]}>
						<div>
							<LockClosedIcon />
							<div>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>Historique prestataire</Typography>
								<Typography typo={ITypo.P_SMALL}>
									{productSubscribed.legacyReadOnly
										? "Cette opération reprise est verrouillée en lecture seule. Vous pouvez l’archiver : les autres opérations du client ne changent pas."
										: "Cette opération provient de l’historique prestataire."}
								</Typography>
							</div>
						</div>
						{legacyHistoryPath && (
							<Button variant={EButtonVariant.OUTLINED} onClick={() => navigate(legacyHistoryPath)}>
								Consulter l’historique
							</Button>
						)}
					</div>
				)}
				<div className={classes["subscription-header"]}>
					<div className={classes["information-container"]}>
						<div className={classes["subscription-information"]}>
							<div className={classNames(classes["collumn"], classes["left"])}>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.subscription_type}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.detention_percentage}</Typography>
								{productSubscribed.subscriptionProductType !== ESubscriptionProductType.pleine_propriete && (
									<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.contrepartie}</Typography>
								)}
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.subscribed_amount}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.cgp}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.email_cgp}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.email_administratif}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.cabinet}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.membre_reseau}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.scpi}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.nbParts}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.reception_date}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.validation_date}</Typography>
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.due_date}</Typography>
								{demembrementEndDate && (
									<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.demembrement_end_date}</Typography>
								)}
								<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.mo_subscription_summary.payout_date}</Typography>
							</div>
							<div className={classNames(classes["collumn"], classes["right"])}>
								<Typography typo={ITypo.P_MEDIUM}>{subscriptionTypeLabel}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{detentionPercentage}</Typography>
								{productSubscribed.subscriptionProductType !== ESubscriptionProductType.pleine_propriete && (
									<div className={classes["contrepartie-value"]}>
										<Typography typo={ITypo.P_MEDIUM}>
											{productSubscribed.contrepartieNom ?? I18n.asset.pages.mo_subscription_summary.contrepartie_sans}
										</Typography>
										<button
											className={classes["edit-icon-button"]}
											onClick={contrepartieModal.open}
											title="Modifier la contrepartie"
											disabled={productSubscribed.legacyReadOnly}>
											<PencilSquareIcon width={16} height={16} />
										</button>
									</div>
								)}
								<Typography typo={ITypo.P_MEDIUM}>
									{productSubscribed.amountAtSubscription !== undefined
										? NumberUtils.formatNumberAddingSpaces(productSubscribed.amountAtSubscription, 2) + " €"
										: "_"}
								</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{cgpName}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{cgp?.email ?? "-"}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{cgp?.offices![0]?.emailAdminitratif ?? "-"}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{cgp?.offices![0]?.raisonSociale ?? "-"}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{cgp?.offices![0]?.membreReseau ?? "-"}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{productSubscribed.productSheet?.name}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{productSubscribed.quantity}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{productSubscribed.submittedAt ? new Date(productSubscribed.submittedAt).toLocaleDateString() : "-"}</Typography>
								<Typography typo={ITypo.P_MEDIUM}>
									{productSubscribed.validationDate ? new Date(productSubscribed.validationDate).toLocaleDateString() : "-"}
								</Typography>
								<Typography typo={ITypo.P_MEDIUM}>{dueDate?.toLocaleDateString() ?? "-"}</Typography>
								{demembrementEndDate && (
									<Typography typo={ITypo.P_MEDIUM}>{demembrementEndDate.toLocaleDateString()}</Typography>
								)}
								<Typography typo={ITypo.P_MEDIUM}>{payoutDate ?? "-"}</Typography>
							</div>
						</div>
						<div className={classes["statut-container"]}>
							<MuiSelectInputElement
								name="status"
								options={statusOptions}
								label="Statut"
								className={classes["statut-input"]}
								defaultValue={{
									id: productSubscribed?.step?.toString() ?? "",
									label: I18n.asset.enums.ESubStepMO[productSubscribed?.step ?? ESubStep.processing],
								}}
								disabled={productSubscribed.legacyReadOnly}
								onChange={onStatusChange}
							/>
							{productSubscribed.subscription?.rtoDocumentsStatus === EDocumentValidationStatus.anomalyReported && (
								<div className={classes["product-signage"]}>
									<Typography typo={ITypo.P_MEDIUM} className={classes["anomaly"]}>
										{I18n.asset.pages.mo_subscription_summary.rto_anomaly}
									</Typography>
									<textarea
										className={classes["signage-textarea"]}
										name="errorMessage"
										value={productSubscribed.subscription?.rtoAnomalyReported?.message ?? ""}
										disabled
									/>
								</div>
							)}
							{productSubscribed.documentsValidatedStatus === EDocumentValidationStatus.anomalyReported && (
								<div className={classes["product-signage"]}>
									<Typography typo={ITypo.P_MEDIUM} className={classes["anomaly"]}>
										{I18n.asset.pages.mo_subscription_summary.product_anomaly}
									</Typography>
									<textarea className={classes["signage-textarea"]} name="errorMessage" value={productSubscribed.anomalyReport?.message ?? ""} disabled />
								</div>
							)}
						</div>
					</div>
					{hasEsignature && <StatusTracker productSubscribedId={productSubscribedId} />}
				</div>

				{!hasEsignature && (
					<div className={classes["buttons-container"]}>
						<div className={classes["button-container"]}>
							<Button disabled={productSubscribed.legacyReadOnly} onClick={sendLegalDocs}>
								{I18n.asset.pages.mo_subscription_summary.send_legal_docs}
							</Button>
							{getManuallySentLegalDocumentsAt() !== null && (
								<Typography typo={ITypo.P_MEDIUM} className={classes["send-docs-info"]}>
									{`${I18n.asset.pages.mo_subscription_summary.send_legal_docs_at} ${getManuallySentLegalDocumentsAt()}`}
								</Typography>
							)}
						</div>
						<div className={classes["button-container"]}>
							<Button disabled={productSubscribed.legacyReadOnly} onClick={notifyCGPTransfer}>
								{I18n.asset.pages.mo_subscription_summary.notify_cgp_transfer}
							</Button>
							{getManuallyTransferToSGAt() !== null && (
								<Typography typo={ITypo.P_MEDIUM} className={classes["send-docs-info"]}>
									{`${I18n.asset.pages.mo_subscription_summary.notify_cgp_transfer_at} ${getManuallyTransferToSGAt()}`}
								</Typography>
							)}
						</div>
					</div>
				)}

				<NavTabOutlet
					items={[
						{
							label: I18n.asset.pages.mo_subscription_summary.sub_pages.subscribers.page_title,
							path: modules.pages.MOSubscriptionSummary.props.pages.Subscribers.props.path.replace(":productSubscribedId", productSubscribedId),
						},
						{
							label: I18n.asset.pages.mo_subscription_summary.sub_pages.payment.page_title,
							path: modules.pages.MOSubscriptionSummary.props.pages.Payment.props.path.replace(":productSubscribedId", productSubscribedId),
						},
						{
							label: I18n.asset.pages.mo_subscription_summary.sub_pages.bulletin.page_title,
							path: modules.pages.MOSubscriptionSummary.props.pages.Bulletin.props.path.replace(":productSubscribedId", productSubscribedId),
						},
						{
							label: I18n.asset.pages.mo_subscription_summary.sub_pages.files.page_title,
							path: modules.pages.MOSubscriptionSummary.props.pages.Files.props.path.replace(":productSubscribedId", productSubscribedId),
						},
						{
							label: I18n.asset.pages.mo_subscription_summary.sub_pages.signable_documents.page_title,
							path: modules.pages.MOSubscriptionSummary.props.pages.SignableDocuments.props.path.replace(":productSubscribedId", productSubscribedId),
						},
						{
							label: I18n.asset.pages.mo_subscription_summary.sub_pages.signed_documents.page_title,
							path: modules.pages.MOSubscriptionSummary.props.pages.SignedDocuments.props.path.replace(":productSubscribedId", productSubscribedId),
						},
					]}
					outletContext={outletContext}
				/>
			</div>
			<ValidationDateModal isOpen={isOpen} onClose={close} productSubscribedId={productSubscribed.id} onSubmitSuccess={onValidationDateSubmitSuccess} />
			<Snackbar
				open={openSuccessAlert.isOpen}
				autoHideDuration={6000}
				onClose={() => setOpenSuccessAlert({ isOpen: false, message: "" })}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}>
				<Alert severity="success" sx={{ width: "100%" }} onClose={() => setOpenSuccessAlert({ isOpen: false, message: "" })}>
					{openSuccessAlert.message}
				</Alert>
			</Snackbar>
			<ArchiveSubscriptionModal
				isOpen={archiveSubscriptionModal.isOpen}
				onClose={archiveSubscriptionModal.close}
				subscriptionId={productSubscribed.subscriptionId}
				isLegacy={Boolean(productSubscribed.isLegacy) && subscription?.productsSubscribed?.length === 1}
			/>
			<ContrepartieModal
				isOpen={contrepartieModal.isOpen}
				onClose={contrepartieModal.close}
				productSubscribedId={productSubscribed.id}
				currentContrepartieNom={productSubscribed.contrepartieNom ?? null}
				onSaveSuccess={setProductSubscribed}
			/>
		</PageTemplate>
	);
}
